// Multi-audience organizational knowledge. Authoring DTOs power the internal
// library/editor; Reader* payloads are safe channel-specific projections.

export type KbArticleStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
export type KbArticleAudience = 'INTERNAL' | 'PORTAL' | 'PUBLIC'
export type KbArticleDiscoverability = 'LISTED' | 'UNLISTED'

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface KbSettingsDTO {
  id: string
  organizationId: string
  /** Public site 404s until this is on */
  enabled: boolean
  title: string
  description: string | null
  accentColor: string | null
  createdAt: string
  updatedAt: string
}

export interface UpdateKbSettingsDTO {
  enabled?: boolean
  title?: string
  description?: string | null
  accentColor?: string | null
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export interface KbCategoryDTO {
  id: string
  organizationId: string
  name: string
  slug: string
  description: string | null
  order: number
  /** Non-deleted articles in this category (all statuses) */
  articleCount: number
  createdAt: string
  updatedAt: string
}

export interface CreateKbCategoryDTO {
  name: string
  description?: string | null
}

export interface UpdateKbCategoryDTO {
  name?: string
  description?: string | null
}

export interface ReorderKbCategoriesDTO {
  /** Full ordered list of category ids; order = array index */
  categoryIds: string[]
}

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

export interface KbArticleSummaryDTO {
  id: string
  categoryId: string | null
  title: string
  slug: string
  excerpt: string | null
  status: KbArticleStatus
  audience: KbArticleAudience
  discoverability: KbArticleDiscoverability
  aiTeamEnabled: boolean
  aiCustomerEnabled: boolean
  /** Template rows never publish and never appear in article lists/readers. */
  isTemplate: boolean
  order: number
  viewCount: number
  publishedAt: string | null
  archivedAt: string | null
  updatedAt: string
}

export interface KbArticleDTO extends KbArticleSummaryDTO {
  organizationId: string
  contentHtml: string
  contentJson: unknown | null
  createdById: string
  updatedById: string | null
  createdAt: string
}

export interface CreateKbArticleDTO {
  title: string
  categoryId?: string | null
  audience?: KbArticleAudience
  /**
   * Seed the new article's content from a template: an org template's
   * article id, or a system template id (`system:` prefix). Images in an
   * org template are copied into the new article's own S3 namespace.
   */
  templateId?: string | null
}

export interface CreateKbTemplateDTO {
  title: string
  /** Optional system template (`system:` id) to seed the content from. */
  templateId?: string | null
}

export interface UpdateKbArticleDTO {
  title?: string
  categoryId?: string | null
  contentHtml?: string
  contentJson?: unknown
  /** Cleared (re-derived from content) when set to an empty string */
  excerpt?: string | null
  audience?: KbArticleAudience
  discoverability?: KbArticleDiscoverability
  aiTeamEnabled?: boolean
  aiCustomerEnabled?: boolean
}

export interface ReorderKbArticlesDTO {
  /** null = uncategorized group */
  categoryId: string | null
  /** Full ordered list of article ids in that group; order = array index */
  articleIds: string[]
}

// ---------------------------------------------------------------------------
// Public site
// ---------------------------------------------------------------------------

export interface PublicKbArticleSummaryDTO {
  title: string
  slug: string
  excerpt: string | null
}

export interface PublicKbCategoryDTO {
  name: string
  slug: string
  description: string | null
  articles: PublicKbArticleSummaryDTO[]
}

export interface PublicKbSiteDTO {
  organizationName: string
  title: string
  description: string | null
  accentColor: string | null
  categories: PublicKbCategoryDTO[]
  /** Published articles without a category, listed after the categories */
  uncategorized: PublicKbArticleSummaryDTO[]
}

export interface PublicKbArticleDTO {
  title: string
  slug: string
  contentHtml: string
  categoryName: string | null
  updatedAt: string
}

/** Reader payloads are shared by internal and portal libraries. */
export type ReaderKbArticleSummaryDTO = PublicKbArticleSummaryDTO
export type ReaderKbCategoryDTO = PublicKbCategoryDTO
export type ReaderKbSiteDTO = PublicKbSiteDTO
export type ReaderKbArticleDTO = PublicKbArticleDTO
export type ReaderKbSearchResultDTO = PublicKbSearchResultDTO

export interface PublicKbSearchResultDTO {
  title: string
  slug: string
  excerpt: string | null
}

// ---------------------------------------------------------------------------
// Article templates
// ---------------------------------------------------------------------------
// Org templates are real KbArticle rows flagged isTemplate, edited in the
// normal article editor. System starter templates are code-defined below,
// not database rows; their ids carry the `system:` prefix. Authored as HTML
// (the article editor works on an HTML string; TipTap JSON is derived on
// first save).

export const SYSTEM_KB_TEMPLATE_PREFIX = 'system:'

export const isSystemKbTemplateId = (id: string): boolean =>
  id.startsWith(SYSTEM_KB_TEMPLATE_PREFIX)

export interface SystemKbTemplate {
  id: string
  name: string
  description: string
  html: string
}

export const SYSTEM_KB_TEMPLATES: SystemKbTemplate[] = [
  {
    id: 'system:sop',
    name: 'Standard Operating Procedure',
    description:
      'Step-by-step procedure with purpose, scope, and troubleshooting.',
    html: [
      '<h2>Purpose</h2>',
      '<p>Describe what this procedure accomplishes and why it exists.</p>',
      '<h2>Scope</h2>',
      '<p>Who this applies to, and when it should be followed.</p>',
      '<h2>Prerequisites</h2>',
      '<ul><li>Access or permissions required</li><li>Tools or materials needed</li></ul>',
      '<h2>Procedure</h2>',
      '<ol>',
      '<li><p><strong>Step name.</strong> Describe the action to take. Add a screenshot below the step if it helps.</p></li>',
      '<li><p><strong>Step name.</strong> Keep each step to a single action where possible.</p></li>',
      '<li><p><strong>Step name.</strong> Note the expected result so the reader can confirm they are on track.</p></li>',
      '</ol>',
      '<h2>Troubleshooting</h2>',
      '<p>Common problems and how to resolve them.</p>',
      '<h2>Revision history</h2>',
      '<ul><li>Date, what changed, and who changed it</li></ul>',
    ].join(''),
  },
  {
    id: 'system:runbook',
    name: 'Runbook',
    description:
      'Operational runbook for responding to a recurring situation or incident.',
    html: [
      '<h2>Overview</h2>',
      '<p>What situation this runbook covers and the expected outcome.</p>',
      '<h2>When to use</h2>',
      '<p>Triggers or symptoms that mean this runbook applies.</p>',
      '<h2>Contacts</h2>',
      '<ul><li>Owner:</li><li>Escalation:</li></ul>',
      '<h2>Steps</h2>',
      '<ol>',
      '<li><p>Verify the situation. List the checks that confirm this runbook applies.</p></li>',
      '<li><p>Take the corrective action.</p></li>',
      '<li><p>Confirm resolution and record what happened.</p></li>',
      '</ol>',
      '<h2>Rollback</h2>',
      '<p>How to undo the actions above if they make things worse.</p>',
    ].join(''),
  },
  {
    id: 'system:how-to',
    name: 'How-to guide',
    description: 'Short task-focused guide answering one question.',
    html: [
      '<h2>What this covers</h2>',
      '<p>The single task this guide walks through, and who it is for.</p>',
      '<h2>Before you start</h2>',
      '<ul><li>Anything the reader needs ready first</li></ul>',
      '<h2>Steps</h2>',
      '<ol>',
      '<li><p>First action.</p></li>',
      '<li><p>Second action.</p></li>',
      '<li><p>What success looks like.</p></li>',
      '</ol>',
      '<h2>Related</h2>',
      '<p>Link related articles or the SOP this guide summarizes.</p>',
    ].join(''),
  },
]
